import React from 'react';
import { get } from 'lodash';
import Draggable from 'react-draggable';
import { AutoSizer, MultiGrid } from 'react-virtualized';

import 'react-virtualized/styles.css';
import './schema-editor/schema-browser/SchemaBrowserGrid.css';

const MIN_CELL_WIDTH = 20;
const MIN_CELL_HEIGHT = 20;

class ResizableGrid extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      initialColumnWidth: null,
      columnWidths: {},

      initialRowHeight: null,
      rowHeights: {},
    };

    this._cellRenderer = this._cellRenderer.bind(this);
    this._getRowHeight = this._getRowHeight.bind(this);
    this._getColumnWidth = this._getColumnWidth.bind(this);
    this._onDragXStart = this._onDragXStart.bind(this);
    this._onDragXStop = this._onDragXStop.bind(this);
    this._onDragYStart = this._onDragYStart.bind(this);
    this._onDragYStop = this._onDragYStop.bind(this);
  }

  getSnapshotBeforeUpdate = (prevProps, prevState) => {
    const { columnWidths, rowHeights } = prevState;

    if (
      this._grid &&
      (rowHeights !== this.state.rowHeights || columnWidths !== this.state.columnWidths)
    ) {
      return true;
    }

    return null;
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (snapshot === true) {
      this._grid.recomputeGridSize();
    }
  }

  render() {
    const {
      className,
      rowCount,
      overscanRowCount,
      columnCount,
      overscanColumnCount,
      fixedRowCount,
      fixedColumnCount,
    } = this.props;

    return (
      <AutoSizer>
        {({ width, height }) => (
          <MultiGrid
            ref={(g) => {
              this._grid = g;

              if (this.props.onRef) {
                this.props.onRef(g, this);
              }
            }}
            classNameBottomLeftGrid={className}
            classNameBottomRightGrid={className}
            classNameTopLeftGrid={className}
            classNameTopRightGrid={className}
            width={width}
            height={height}
            fixedRowCount={fixedRowCount}
            fixedColumnCount={fixedColumnCount}
            rowCount={rowCount}
            rowHeight={this._getRowHeight}
            overscanRowCount={overscanRowCount}
            columnCount={columnCount}
            columnWidth={this._getColumnWidth}
            overscanColumnCount={overscanColumnCount}
            cellRenderer={this._cellRenderer}
          />
        )}
      </AutoSizer>
    );
  }

  _cellRenderer(cell) {
    const {
      getCellContent,
      getAllowColumnResize,
      getAllowRowResize,
      getDefaultColumnWidth,
      getDefaultRowHeight,
      getRowId,
    } = this.props;
    const { columnWidths, rowHeights } = this.state;
    const { key, columnIndex, rowIndex, style } = cell;
    const cellContent = getCellContent(cell);
    const allowColumnResize = getAllowColumnResize ? getAllowColumnResize(cell) : true;
    const allowRowResize = getAllowRowResize ? getAllowRowResize(cell) : true;
    const rowId = getRowId(rowIndex);
    let minX = 0;
    let minY = 0;

    if (allowColumnResize) {
      minX = MIN_CELL_WIDTH - get(columnWidths, columnIndex, getDefaultColumnWidth(columnIndex));
    }

    if (allowRowResize) {
      minY = MIN_CELL_HEIGHT - get(rowHeights, rowId, getDefaultRowHeight(rowIndex));
    }

    return (
      <div key={key} style={style}>
        {cellContent}
        {allowColumnResize && (
          <div>
            <div className="grid-resize-handle-x" />
            <Draggable
              axis="x"
              defaultClassName="grid-resize-handle-x"
              defaultClassNameDragging="grid-resize-handle-x-active"
              bounds={{ left: minX }}
              position={{
                x: 0,
                y: 0,
              }}
              onStart={(event, data) => this._onDragXStart(columnIndex, rowIndex, rowId, data)}
              onStop={(event, data) => this._onDragXStop(columnIndex, rowIndex, rowId, data)}
            >
              <div />
            </Draggable>
          </div>
        )}
        {allowRowResize && (
          <div>
            <div className="grid-resize-handle-y" />
            <Draggable
              axis="y"
              defaultClassName="grid-resize-handle-y"
              defaultClassNameDragging="grid-resize-handle-y-active"
              bounds={{ top: minY }}
              position={{
                x: 0,
                y: 0,
              }}
              onStart={(event, data) => this._onDragYStart(columnIndex, rowIndex, rowId, data)}
              onStop={(event, data) => this._onDragYStop(columnIndex, rowIndex, rowId, data)}
            >
              <div />
            </Draggable>
          </div>
        )}
      </div>
    );
  }

  _getRowHeight({ index }) {
    const { getDefaultRowHeight, getRowId } = this.props;
    const { rowHeights } = this.state;
    const rowId = getRowId(index);

    return get(rowHeights, rowId, getDefaultRowHeight(index));
  }

  _getColumnWidth({ index }) {
    const { getDefaultColumnWidth } = this.props;
    const { columnWidths } = this.state;

    return get(columnWidths, index, getDefaultColumnWidth(index));
  }

  _onDragXStart(columnIndex, rowIndex, rowId, data) {
    const { getDefaultColumnWidth } = this.props;
    const { columnWidths } = this.state;

    this.setState({
      initialColumnWidth: get(columnWidths, columnIndex, getDefaultColumnWidth(columnIndex)),
    });
  }

  _onDragXStop(columnIndex, rowIndex, rowId, data) {
    const { initialColumnWidth, columnWidths } = this.state;

    this.setState(
      {
        initialColumnWidth: null,
        columnWidths: {
          ...columnWidths,
          [columnIndex]: initialColumnWidth + data.x,
        },
      },
      () => {
        if (this._grid && this._grid._bottomRightGrid && this.props.onColumnResize) {
          const totalWidth = this._grid._bottomRightGrid.getTotalColumnsWidth();
          this.props.onColumnResize(totalWidth);
        }
      },
    );
  }

  _onDragYStart(columnIndex, rowIndex, rowId, data) {
    const { getDefaultRowHeight } = this.props;
    const { rowHeights } = this.state;

    this.setState({
      initialRowHeight: get(rowHeights, rowId, getDefaultRowHeight(rowIndex)),
    });
  }

  _onDragYStop(columnIndex, rowIndex, rowId, data) {
    const { initialRowHeight, rowHeights } = this.state;

    this.setState({
      initialRowHeight: null,
      rowHeights: {
        ...rowHeights,
        [rowId]: initialRowHeight + data.y,
      },
    });
  }
}

export default ResizableGrid;
